import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppConfig } from '../lib/appConfig';
import NotificationBell from './NotificationBell';

const STAFF_NAV = [
  {
    section: 'OVERVIEW',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: '⬡' },
    ],
  },
  {
    section: 'SCRIPTS',
    items: [
      { to: '/scripts', label: 'Scripts', icon: '✦' },
      { to: '/qual',    label: 'Qual',    icon: '◉' },
    ],
  },
  {
    section: 'PRODUCTION',
    items: [
      { to: '/live',       label: 'Live Production',     icon: '◈', incomplete: true },
      // Hidden while AI Production is disabled (deployment flag, /api/config).
      { to: '/ai',         label: 'AI Production',       icon: '◇', aiFlagged: true },
      { to: '/calendar',   label: 'Production Calendar', icon: '⊞' },
      { to: '/edit-queue', label: 'Edit Queue',           icon: '◫' },
      { to: '/assets',     label: 'Asset Library',        icon: '▣' },
      // Raw footage is INTERNAL material (Owner, 2026-08-19) — the API refuses
      // external users on every footage route, so the link is hidden rather than
      // left pointing at a page that would come back empty. Note the nav is
      // otherwise gated on the Talent ROLE, and 9 of 10 external users do not
      // have it, so without this they would all still see this item.
      { to: '/footage',    label: 'Raw Footage',           icon: '⬡', internalOnly: true },
      // Cold archive — a sibling of Raw Footage, not a view of it. Distinct
      // icon so the two libraries are never confused in the nav.
      { to: '/storage',    label: 'Storage',               icon: '▤' },
    ],
  },
  {
    section: 'MANAGEMENT',
    items: [
      { to: '/scheduler',  label: 'Posting Scheduler', icon: '▦' },
      { to: '/reports',    label: 'Reports',            icon: '▤' },
      { to: '/assistant',  label: 'Clio',               icon: '◎' },
      { to: '/admin',      label: 'Admin',              icon: '⚙' },
    ],
  },
];

const TALENT_NAV = [
  {
    section: 'MY PORTAL',
    items: [
      { to: '/talent',              label: 'Dashboard',     icon: '⬡' },
      { to: '/talent/availability', label: 'My Availability', icon: '⊞' },
      { to: '/talent/scripts',      label: 'My Scripts',    icon: '✦' },
    ],
  },
];

export default function Sidebar({ incompleteCount = 0, mobileOpen = false, onMobileClose }) {
  const { user, logout } = useAuth();
  const { aiProductionEnabled } = useAppConfig();
  const navigate = useNavigate();
  const roles        = user?.roles || [];
  const hasTalent    = roles.includes('Talent');
  const isTalentOnly = hasTalent && roles.length === 1;
  // Dual-role users (Talent + any staff role) see the staff nav with a link
  // back to their own Talent Portal.
  const isExternal = !!user?.isExternal;

  // Drop internal-only destinations for an external user. Storage deliberately
  // stays: a client's own vault is theirs to use, and the archive folder inside it
  // is hidden server-side by archive_folder.is_internal_only.
  const staffNav = STAFF_NAV
    .map(group => ({
      ...group,
      items: group.items.filter(i =>
        (!i.internalOnly || !isExternal) && (!i.aiFlagged || aiProductionEnabled)),
    }))
    .filter(group => group.items.length > 0);

  const NAV = isTalentOnly
    ? TALENT_NAV
    : hasTalent
      ? [...staffNav, {
          section: 'TALENT',
          items: [{ to: '/talent', label: 'My Talent Portal', icon: '◐' }],
        }]
      : staffNav;

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebarCollapsed') === 'true'; } catch { return false; }
  });

  const toggleCollapsed = () => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem('sidebarCollapsed', String(next)); } catch {}
      return next;
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = user?.initials || user?.name?.charAt(0).toUpperCase() || '?';
  const primaryRole = user?.roles?.[0] || '';

  if (collapsed && !mobileOpen) {
    return (
      <aside className="sidebar sidebar-collapsed">
        {/* Logo mark */}
        <div style={{ padding: '20px 0 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', borderBottom: '1px solid var(--border)', gap: 10 }}>
          <div className="logo-mark">HF</div>
          <button
            onClick={toggleCollapsed}
            title="Expand sidebar"
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', color: 'var(--tx-secondary)', fontSize: 18, padding: '4px 8px', lineHeight: 1 }}
          >
            ›
          </button>
        </div>

        {/* Nav icons */}
        <nav style={{ flex: 1, padding: '10px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, overflowY: 'auto' }}>
          {NAV.map(group => group.items.map(item => (
            <div key={item.to} className="sidebar-tooltip-wrap">
              <NavLink
                to={item.to}
                className={({ isActive }) => `nav-item-icon${isActive ? ' active' : ''}`}
                onClick={item.to === '/scripts' ? (e) => { e.preventDefault(); navigate(item.to); } : undefined}
                title={item.label}
              >
                <span className="nav-icon" style={{ fontSize: 17 }}>{item.icon}</span>
                {item.incomplete && incompleteCount > 0 && (
                  <span style={{ position: 'absolute', top: 2, right: 2, width: 8, height: 8, borderRadius: '50%', background: 'var(--s-incomplete)', display: 'block' }} />
                )}
              </NavLink>
              <span className="sidebar-tooltip">{item.label}</span>
            </div>
          )))}
        </nav>

        <NotificationBell collapsed />

        {/* Help button */}
        <div className="sidebar-tooltip-wrap" style={{ marginBottom: 4 }}>
          <button
            onClick={() => navigate('/assistant', { state: { helpMode: true } })}
            title="Help — ask Clio"
            className="nav-item-icon"
            style={{ fontSize: 15, fontWeight: 700 }}
          >
            ?
          </button>
          <span className="sidebar-tooltip">Help</span>
        </div>

        {/* User avatar */}
        <div style={{ padding: '12px 0', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <div className="sidebar-tooltip-wrap">
            <NavLink to="/account" style={{ textDecoration: 'none' }} title={user?.name || 'Account'}>
              <div className="user-avatar">{initials}</div>
            </NavLink>
            <span className="sidebar-tooltip">{user?.name || 'Account'}</span>
          </div>
          <button
            onClick={handleLogout}
            title="Sign out"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx-muted)', fontSize: 14, padding: 4, lineHeight: 1 }}
          >
            ⏻
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside className={`sidebar${mobileOpen ? ' mobile-open' : ''}`}>
      <div className="sidebar-top">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="logo">
            <div className="logo-mark">HF</div>
            <div>
              <div className="logo-name">Helicon Flow</div>
              <div className="logo-sub">Journey Viral</div>
            </div>
          </div>
          <button
            onClick={toggleCollapsed}
            title="Collapse sidebar"
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', color: 'var(--tx-secondary)', fontSize: 18, padding: '4px 8px', lineHeight: 1, flexShrink: 0 }}
          >
            ‹
          </button>
        </div>
        <div className="sidebar-search">
          <span style={{ color: 'var(--tx-muted)', fontSize: 13 }}>⌕</span>
          <input placeholder="Search…" readOnly />
        </div>
      </div>

      <nav className="sidebar-nav">
        {NAV.map(group => (
          <div key={group.section}>
            <div className="nav-section-label">{group.section}</div>
            {group.items.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                onClick={item.to === '/scripts'
                  ? (e) => { e.preventDefault(); navigate(item.to); onMobileClose?.(); }
                  : () => onMobileClose?.()
                }
              >
                <span className="nav-icon">{item.icon}</span>
                {item.label}
                {item.incomplete && incompleteCount > 0 && (
                  <span className="nav-badge">{incompleteCount}</span>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <NotificationBell />

      {/* Help button */}
      <button
        onClick={() => navigate('/assistant', { state: { helpMode: true } })}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px',
          background: 'none', border: 'none', cursor: 'pointer', width: '100%',
          color: 'var(--tx-muted)', fontSize: 13, fontFamily: 'var(--font)',
          borderTop: '1px solid var(--border)',
        }}
      >
        <span style={{
          width: 22, height: 22, borderRadius: '50%', background: 'var(--bg-hover)',
          border: '1px solid var(--border-mid)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0,
          color: 'var(--tx-secondary)',
        }}>?</span>
        <span>Help</span>
      </button>

      <div className="sidebar-user">
        <NavLink
          to="/account"
          style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, textDecoration: 'none' }}
        >
          <div className="user-avatar">{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="user-name">{user?.name || 'User'}</div>
            <div className="user-role">{primaryRole}</div>
          </div>
        </NavLink>
        <button
          onClick={handleLogout}
          title="Sign out"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--tx-muted)', fontSize: 14, padding: '4px',
            flexShrink: 0, lineHeight: 1,
          }}
        >
          ⏻
        </button>
      </div>
    </aside>
  );
}
